import {
  deleteVideo,
  getFolders,
  getVideo,
  mergeVideoEntities,
  updateVideo,
  updateVideoChapters,
  getVideos,
} from 'lib/api';
import {
  FetchMoreVideosParams,
  GLOBAL_ACTIONS,
  HandleDeleteVideoParams,
  HandleUpdateVideoParams,
  InitLibraryParams,
  SelectFolderParams,
  HandleUpdateVideoChaptersParams,
} from '.';
import { VIDEOS_ACTIONS } from './videosReducer';
import { SHARED_FOLDER_PREFIX } from 'lib/const/Folders';

const {
  INIT_LIBRARY_START,
  INIT_LIBRARY_SUCCESS,
  GET_FOLDERS_ERROR,
  GET_VIDEOS_START,
  GET_VIDEOS_SUCCESS,
  GET_VIDEOS_ERROR,
  SET_LIBRARY_FOLDER,
  DELETE_VIDEOS_SUCCESS,
  DELETE_VIDEOS_ERROR,
  UPDATE_VIDEO_ERROR,
  UPDATE_VIDEOS_SUCCESS,
} = GLOBAL_ACTIONS;

const {
  GET_VIDEO_START,
  GET_VIDEO_ERROR,
  GET_VIDEO_SUCCESS,
  MERGE_VIDEO_START,
  MERGE_VIDEO_ERROR,
  MERGE_VIDEO_SUCCESS,
} = VIDEOS_ACTIONS;

export const initLibrary = async ({
  dispatch = () => {},
  page = 0,
  size = 10,
  currentFolder,
  search = '',
}: InitLibraryParams): Promise<void> => {
  dispatch({ type: INIT_LIBRARY_START });
  const folders = await getFolders({ getShared: true }).catch(err => err);
  if (folders instanceof Error) {
    dispatch({
      type: GET_FOLDERS_ERROR,
      continueLoading: true,
      error: folders.message,
    });
    return;
  }

  let loadedState = {
    globalStats: {},
    folders,
    currentFolder: currentFolder || folders[0],
  };

  await fetchMoreVideos({
    size,
    page,
    search,
    folder: loadedState.currentFolder,
    dispatch,
  });

  dispatch({
    type: INIT_LIBRARY_SUCCESS,
    continueLoading: false,
    payload: loadedState,
  });
};

export const handleUpdateVideo = async ({
  videoId,
  videoBody,
  dispatch,
}: HandleUpdateVideoParams): Promise<void> => {
  dispatch({ type: UPDATE_VIDEO_ERROR, continueLoading: true });
  const result = await updateVideo({ videoId, videoBody }).catch(err => err);
  if (result instanceof Error) {
    dispatch({
      type: UPDATE_VIDEO_ERROR,
      continueLoading: false,
      error: result.message,
    });
  } else {
    dispatch({
      type: UPDATE_VIDEOS_SUCCESS,
      payload: result,
      continueLoading: false,
      error: result.message,
    });
  }
};

export const handleUpdateVideoChapters = async ({
  videoId,
  videoBody,
  dispatch,
}: HandleUpdateVideoChaptersParams): Promise<void> => {
  dispatch({ type: UPDATE_VIDEO_ERROR, continueLoading: true });
  const result = await updateVideoChapters({ videoId, videoBody }).catch(
    err => err
  );
  if (result instanceof Error) {
    return dispatch({
      type: UPDATE_VIDEO_ERROR,
      continueLoading: false,
      error: result.message,
    });
  }
  dispatch({
    type: UPDATE_VIDEOS_SUCCESS,
    payload: { video: result },
    continueLoading: false,
    error: result.message,
  });
};
export const fetchMoreVideos = async ({
  folder,
  size,
  page,
  search,
  sort,
  filter,
  dispatch,
  isFolderShared = false,
}: FetchMoreVideosParams): Promise<void> => {
  dispatch({ type: GET_VIDEOS_START, continueLoading: true });

  let folderName = folder?.folderId === '_all' ? '_all' : folder?.name;
  if (isFolderShared) folderName = `${SHARED_FOLDER_PREFIX}${folder?.folderId}`; // ONLY for shared folders

  const videoData = await getVideos({
    folder: folderName,
    size,
    page,
    search,
    sort,
    filter,
  }).catch(err => err);
  if (videoData instanceof Error) {
    dispatch({
      type: GET_VIDEOS_ERROR,
      continueLoading: false,
      error: videoData.message,
    });
    return;
  }
  const { videos, count } = videoData;

  const payload = {
    count,
    folder: folder?.name || 'Primary',
    videos,
    page,
    size,
    search: search || '',
  };
  dispatch({ type: GET_VIDEOS_SUCCESS, continueLoading: false, payload });
  return videoData;
};

export const selectFolder = ({
  selectedFolder,
  dispatch,
}: SelectFolderParams): void => {
  dispatch({
    type: SET_LIBRARY_FOLDER,
    payload: selectedFolder || undefined,
    continueLoading: false,
  });
};

export const handleDeleteVideo = async ({
  videoIds: videosToDelete,
  companyVideos,
  dispatch = () => {},
}: HandleDeleteVideoParams): Promise<void> => {
  const deleteMultiple: boolean = videosToDelete.length > 1;

  const videoIds: string[] = deleteMultiple
    ? (videosToDelete as number[]).map((id: number) => `${id}`)
    : (videosToDelete as string[]);

  const deleteResult = await deleteVideo({
    videoIds: videosToDelete,
    deleteMultiple,
    companyVideos,
  }).catch(err => err);
  if (deleteResult instanceof Error) {
    dispatch({
      type: DELETE_VIDEOS_ERROR,
      error: deleteResult.message,
      continueLoading: false,
    });
  } else {
    const payload = {
      ids: videoIds,
      message: `Video${
        videoIds.length > 1 ? 's' : ''
      } successfully moved to recently deleted bin.`,
    };
    dispatch({ type: DELETE_VIDEOS_SUCCESS, payload, continueLoading: false });
  }
};

export const fetchVideo = async ({ videoId, dispatch }: any): Promise<void> => {
  dispatch({ type: GET_VIDEO_START, continueLoading: true });

  const video = await getVideo(videoId).catch(err => err);
  if (video instanceof Error) {
    dispatch({
      type: GET_VIDEO_ERROR,
      continueLoading: false,
      error: video.message,
    });
    return;
  }
  const payload = {
    video: { ...video, id: videoId },
  };
  dispatch({ type: GET_VIDEO_SUCCESS, continueLoading: false, payload });
};

export const mergeVideos = async ({ data, dispatch }: any): Promise<void> => {
  dispatch({ type: MERGE_VIDEO_START, continueLoading: true });

  const response = await mergeVideoEntities(data).catch(err => err);
  if (response instanceof Error) {
    dispatch({
      type: MERGE_VIDEO_ERROR,
      continueLoading: false,
      error: response.message,
    });
    return;
  }
  const payload = {
    response,
  };
  dispatch({ type: MERGE_VIDEO_SUCCESS, continueLoading: false, payload });
};
